import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Plus, X, CalendarDays, Clock, ShieldCheck, User } from "lucide-react";
import { toast } from "sonner";
import { DatePicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";
import { useModalGuard, UnsavedGuardBar } from "@/hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";

const statusMeta = {
  pending_manager: { label: "Menunggu Manager", chip: "bg-amber-100 text-amber-800" },
  pending_hr: { label: "Menunggu HR", chip: "bg-sky-100 text-sky-800" },
  approved: { label: "Disetujui", chip: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Ditolak", chip: "bg-rose-100 text-rose-800" },
  pending: { label: "Menunggu", chip: "bg-amber-100 text-amber-800" },
};

export default function MyCuti() {
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ jenis: "Tahunan", tanggal_mulai: "", tanggal_selesai: "", jml_hari: 1, keterangan: "", use_skd: false, surat_dokter_url: "" });

  const load = () => api.get("/my/cuti").then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (form.use_skd && !form.surat_dokter_url.trim()) {
        toast.error("SKD wajib melampirkan URL surat dokter"); return;
      }
      await api.post("/my/cuti", form);
      toast.success(form.jml_hari > 3 ? "Pengajuan dikirim → butuh approval Manager lalu HR" : "Pengajuan dikirim → menunggu approval HR");
      setModal(false); setForm({ jenis: "Tahunan", tanggal_mulai: "", tanggal_selesai: "", jml_hari: 1, keterangan: "", use_skd: false, surat_dokter_url: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  // Form 6 field (jenis, tanggal, SKD, keterangan) — JSX modal ada di
  // komponen halaman yang tidak unmount, jadi `active` mengikuti `modal`.
  // Dideklarasikan SEBELUM early-return "loading" di bawah supaya urutan
  // hook React tetap konsisten tiap render.
  const cutiGuard = useModalGuard({
    active: modal, dangerous: true,
    onClose: () => setModal(false), onSubmit: save,
  });
  // Urutan bawaan dipertahankan sampai header diklik. Dideklarasikan sebelum
  // early-return "loading" di bawah supaya urutan hook React tetap konsisten.
  const { sortKey, sortDir, toggle } = useSortState();

  if (!data) return <div className="p-4 md:p-8 max-w-[1400px]"><LoadingState loading data-testid="my-cuti-loading" /></div>;

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Cuti</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Cuti Saya</h1>
          <p className="mt-2 text-sm text-slate-500">Ajukan cuti dan pantau statusnya di sini.</p>
        </div>
        <button data-testid="btn-my-add-cuti" onClick={() => setModal(true)}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium">
          <Plus className="w-4 h-4" /> Ajukan Cuti
        </button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4 mb-6">
        <Card label="Jatah Cuti" value={`${data.saldo.jatah} hari`} tint="slate" />
        <Card label="Cuti Diambil" value={`${data.saldo.diambil} hari`} tint="amber" />
        <Card label="Sisa Cuti" value={`${data.saldo.sisa} hari`} tint="emerald" />
        <Card label="Jatah SKD" value={`${data.saldo.jatah_skd ?? 12} hari`} tint="slate" />
        <Card label="SKD Diambil" value={`${data.saldo.skd_diambil ?? 0} hari`} tint="rose" />
        <Card label="Sisa SKD" value={`${(data.saldo.jatah_skd ?? 12) - (data.saldo.skd_diambil ?? 0)} hari`} tint="sky" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortTh label="Jenis" sortKeyName="jenis" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="my-cuti-sort-jenis" />
              <SortTh label="Periode" sortKeyName="tanggal_mulai" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="my-cuti-sort-periode" />
              <SortTh label="Hari" sortKeyName="jml_hari" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="my-cuti-sort-hari" />
              <th className="text-left py-3 px-4">Status</th>
              <th className="text-left py-3 px-4">Keterangan</th>
            </tr>
          </thead>
          <tbody>
            {sortRows(data.items, sortKey, sortDir).map(c => {
              const st = c.status || "approved";
              const meta = statusMeta[st] || statusMeta.approved;
              return (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors align-top">
                  <td className="py-3 px-4"><span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700">{c.jenis}</span></td>
                  <td className="py-3 px-4 text-slate-600 text-xs">{c.tanggal_mulai} → {c.tanggal_selesai}</td>
                  <td className="py-3 px-4 text-center font-semibold">{c.jml_hari}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${meta.chip}`}>{meta.label}</span>
                    {(c.manager_approved_by || c.hr_approved_by) && (
                      <div className="mt-1.5 text-xs text-slate-500 space-y-0.5">
                        {c.manager_approved_by && <div className="flex items-center gap-1"><User className="w-3 h-3" /> Manager: {c.manager_approved_by}</div>}
                        {c.hr_approved_by && <div className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> HR: {c.hr_approved_by}</div>}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-slate-500 text-xs">{c.keterangan}</td>
                </tr>
              );
            })}
            {data.items.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-sm text-slate-500">
              <CalendarDays className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              Belum ada pengajuan cuti
            </td></tr>}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={cutiGuard.requestClose}>
          <div ref={cutiGuard.contentRef} className="bg-white rounded-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h3 className="font-display font-semibold text-slate-900">Ajukan Cuti</h3>
              <button onClick={cutiGuard.requestClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Jenis Cuti</span>
                  <select value={form.jenis} onChange={e => setForm(s => ({ ...s, jenis: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    {["Tahunan","Melahirkan","Sakit","Menikah","Duka","Izin"].map(x => <option key={x}>{x}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Jumlah Hari</span>
                  <input type="number" min={1} value={form.jml_hari} onChange={e => setForm(s => ({ ...s, jml_hari: parseInt(e.target.value)||1 }))}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Tgl Mulai</span>
                  <DatePicker value={form.tanggal_mulai} onChange={v => setForm(s => ({ ...s, tanggal_mulai: v }))}
                    className="mt-1" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Tgl Selesai</span>
                  <DatePicker value={form.tanggal_selesai} onChange={v => setForm(s => ({ ...s, tanggal_selesai: v }))}
                    className="mt-1" />
                </label>
              </div>
              {form.jml_hari > 3 && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <Clock className="w-4 h-4 inline mr-1" /> Cuti panjang (&gt;3 hari) — butuh 2 approval berurutan (Manager → HR).
                </div>
              )}
              <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!form.use_skd}
                    onChange={e => setForm(s => ({ ...s, use_skd: e.target.checked, jenis: e.target.checked ? "Sakit" : s.jenis }))}
                    data-testid="input-use-skd"
                    className="w-4 h-4 rounded border-slate-300 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-900">Ambil dari jatah SKD (Surat Keterangan Dokter)</span>
                </label>
                {form.use_skd && (
                  <>
                    <p className="text-[11px] text-emerald-800">
                      Wajib lampirkan surat dokter. Potong dari jatah SKD (bukan cuti biasa).
                    </p>
                    <input type="url" placeholder="URL surat dokter (upload ke Google Drive/Cloud, lalu paste link)"
                      value={form.surat_dokter_url}
                      onChange={e => setForm(s => ({ ...s, surat_dokter_url: e.target.value }))}
                      data-testid="input-surat-dokter"
                      className="w-full h-9 px-2 rounded border border-emerald-300 text-xs" />
                  </>
                )}
              </div>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Keterangan</span>
                <textarea value={form.keterangan} onChange={e => setForm(s => ({ ...s, keterangan: e.target.value }))}
                  className="mt-1 w-full h-20 p-3 rounded-lg border border-slate-200 text-sm resize-none" placeholder="Alasan cuti..." />
              </label>
              <div className="flex justify-end gap-2">
                <button onClick={cutiGuard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                <button onClick={save} data-testid="btn-my-save-cuti" className="h-10 px-5 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium">Ajukan</button>
              </div>
              {cutiGuard.confirming && (
                <UnsavedGuardBar onStay={cutiGuard.cancelClose} onDiscard={cutiGuard.confirmDiscard} testid="my-cuti-guard-bar" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ label, value, tint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{label}</p>
      <p className={`font-display text-2xl font-bold text-${tint}-700`}>{value}</p>
    </div>
  );
}
